// Roadmap — admin-managed product roadmap.
//
// Backend (migration 0035 + modules/roadmap.js):
//   GET   /api/admin/roadmap                 — versions + features + stats
//   POST  /api/admin/roadmap/features        — add a feature to a version
//   PATCH /api/admin/roadmap/features/:id     — update status / name / description
//
// Versions (V1 … V3) each hold a list of features; every feature has a status
// (planned / building / done). Admins change status inline and add features
// per version. A read-only mirror lives in the retailer portal.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type Status = "planned" | "building" | "done";

interface Feature {
  id: string;
  versionId: string;
  name: string;
  description: string;
  status: Status;
  sortOrder: number;
}

interface Version {
  id: string;
  code: string;
  title: string;
  subtitle: string;
  status: string;
  features: Feature[];
}

interface Stats {
  done: number;
  building: number;
  planned: number;
  total: number;
}

interface RoadmapResponse {
  ok?: boolean;
  versions?: Version[];
  stats?: Stats;
}

type Filter = "all" | Status;

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<Status, string> = {
  planned: "Planned",
  building: "Building",
  done: "Shipped",
};

const STATUS_ORDER: Status[] = ["planned", "building", "done"];

function coerceStatus(raw: unknown): Status {
  const v = String(raw ?? "").toLowerCase().trim();
  return v === "done" || v === "building" ? v : "planned";
}

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) => {
    const m: Record<string, string> = {
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    };
    return m[c] ?? c;
  });
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Page ─────────────────────────────────────────────────────────────────────

const page: Page = {
  name: "roadmap",
  label: "Roadmap",
  icon: "🗺️",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let versions: Version[] = [];
    let stats: Stats = { done: 0, building: 0, planned: 0, total: 0 };
    let loading = true;
    let error: string | null = null;
    let filter: Filter = "all";
    // Versions with their inline "add feature" form open.
    const addOpen = new Set<string>();

    function render(): void {
      if (loading) {
        container.innerHTML = `<section class="rm"><div class="rm-loading">Loading roadmap…</div></section>`;
        return;
      }
      if (error) {
        container.innerHTML = `<section class="rm"><div class="rm-error">${esc(error)}</div></section>`;
        return;
      }

      const statCards = [
        { k: "done", label: "Shipped", val: stats.done, cls: "done" },
        { k: "building", label: "In progress", val: stats.building, cls: "building" },
        { k: "planned", label: "Planned", val: stats.planned, cls: "planned" },
        { k: "total", label: "Total", val: stats.total, cls: "total" },
      ]
        .map(
          (s) => `
          <div class="rm-stat rm-stat--${s.cls}">
            <div class="rm-stat-val">${s.val}</div>
            <div class="rm-stat-label">${s.label}</div>
          </div>`,
        )
        .join("");

      const filterPills = (["all", "done", "building", "planned"] as Filter[])
        .map((f) => {
          const label = f === "all" ? "All" : STATUS_LABEL[f];
          return `<button type="button" class="rm-pill${filter === f ? " rm-pill--on" : ""}" data-filter="${f}">${label}</button>`;
        })
        .join("");

      const versionsHtml = versions
        .map((v) => {
          const shown = v.features.filter((f) => filter === "all" || f.status === filter);
          const rows = shown.length
            ? shown.map((f) => featureRow(f)).join("")
            : `<li class="rm-feature rm-feature--empty">No features match this filter.</li>`;
          const addForm = addOpen.has(v.id)
            ? `
            <li class="rm-addform">
              <input type="text" class="rm-add-name" placeholder="New feature name" data-version="${esc(v.id)}" maxlength="200" />
              <select class="rm-add-status" data-version="${esc(v.id)}">
                ${STATUS_ORDER.map((s) => `<option value="${s}">${STATUS_LABEL[s]}</option>`).join("")}
              </select>
              <button type="button" class="rm-add-save" data-version="${esc(v.id)}">Add</button>
              <button type="button" class="rm-add-cancel" data-version="${esc(v.id)}">Cancel</button>
            </li>`
            : "";
          return `
          <section class="rm-version">
            <header class="rm-version-head">
              <div class="rm-version-title">
                <span class="rm-version-code">${esc(v.code)}</span>
                <span class="rm-version-name">${esc(v.title)}</span>
                <span class="rm-badge rm-badge--${esc(v.status)}">${esc(STATUS_LABEL[coerceStatus(v.status)])}</span>
              </div>
              ${v.subtitle ? `<div class="rm-version-sub">${esc(v.subtitle)}</div>` : ""}
            </header>
            <ul class="rm-features">
              ${rows}
              ${addForm}
            </ul>
            ${addOpen.has(v.id) ? "" : `<button type="button" class="rm-add-toggle" data-version="${esc(v.id)}">+ Add feature</button>`}
          </section>`;
        })
        .join("");

      container.innerHTML = `
        <section class="rm">
          <header class="rm-header">
            <h1>Roadmap</h1>
            <p class="rm-sub">What's shipped, what's being built, and what's coming next.</p>
          </header>
          <div class="rm-stats">${statCards}</div>
          <div class="rm-filters">${filterPills}</div>
          <div class="rm-versions">${versionsHtml}</div>
        </section>`;
    }

    function featureRow(f: Feature): string {
      const sel = STATUS_ORDER.map(
        (s) => `<option value="${s}"${f.status === s ? " selected" : ""}>${STATUS_LABEL[s]}</option>`,
      ).join("");
      return `
        <li class="rm-feature rm-feature--${f.status}">
          <span class="rm-dot" aria-hidden="true"></span>
          <div class="rm-feature-body">
            <div class="rm-feature-name">${esc(f.name)}</div>
            ${f.description ? `<div class="rm-feature-desc">${esc(f.description)}</div>` : ""}
          </div>
          <select class="rm-status-select" data-feature="${esc(f.id)}" aria-label="Status for ${esc(f.name)}">
            ${sel}
          </select>
        </li>`;
    }

    async function load(): Promise<void> {
      loading = true;
      error = null;
      render();
      try {
        const resp = await ctx.api<RoadmapResponse>("/admin/roadmap", { signal: ac.signal });
        versions = (resp.versions ?? []).map((v) => ({
          ...v,
          features: (v.features ?? []).map((f) => ({ ...f, status: coerceStatus(f.status) })),
        }));
        stats = resp.stats ?? { done: 0, building: 0, planned: 0, total: 0 };
        loading = false;
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        loading = false;
        error =
          err instanceof ApiError ? `Couldn't load the roadmap (${err.status}).` : "Couldn't load the roadmap.";
        render();
      }
    }

    function recomputeStats(): void {
      const s: Stats = { done: 0, building: 0, planned: 0, total: 0 };
      for (const v of versions) {
        for (const f of v.features) {
          s.total += 1;
          s[f.status] += 1;
        }
      }
      stats = s;
    }

    async function updateStatus(id: string, status: Status): Promise<void> {
      try {
        await ctx.api<RoadmapResponse>(`/admin/roadmap/features/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: JSON.stringify({ status }),
          signal: ac.signal,
        });
        for (const v of versions) {
          const f = v.features.find((x) => x.id === id);
          if (f) f.status = status;
        }
        recomputeStats();
        render();
        ctx.showToast("Status updated", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast("Couldn't update status", "error");
        render();
      }
    }

    async function addFeature(versionId: string, name: string, status: Status): Promise<void> {
      try {
        const resp = await ctx.api<{ feature?: Feature }>("/admin/roadmap/features", {
          method: "POST",
          body: JSON.stringify({ versionId, name, status }),
          signal: ac.signal,
        });
        const f = resp.feature;
        if (f) {
          const v = versions.find((x) => x.id === versionId);
          if (v) v.features.push({ ...f, status: coerceStatus(f.status) });
        }
        addOpen.delete(versionId);
        recomputeStats();
        render();
        ctx.showToast("Feature added", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast("Couldn't add feature", "error");
      }
    }

    // ── Event delegation ───────────────────────────────────────────────────
    function onClick(e: MouseEvent): void {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const pill = t.closest<HTMLElement>("[data-filter]");
      if (pill) {
        filter = (pill.dataset["filter"] as Filter) ?? "all";
        render();
        return;
      }
      const toggle = t.closest<HTMLElement>(".rm-add-toggle");
      if (toggle) {
        const vid = toggle.dataset["version"];
        if (vid) { addOpen.add(vid); render(); }
        return;
      }
      const cancel = t.closest<HTMLElement>(".rm-add-cancel");
      if (cancel) {
        const vid = cancel.dataset["version"];
        if (vid) { addOpen.delete(vid); render(); }
        return;
      }
      const save = t.closest<HTMLElement>(".rm-add-save");
      if (save) {
        const vid = save.dataset["version"];
        if (!vid) return;
        const nameEl = container.querySelector<HTMLInputElement>(`.rm-add-name[data-version="${vid}"]`);
        const statusEl = container.querySelector<HTMLSelectElement>(`.rm-add-status[data-version="${vid}"]`);
        const name = (nameEl?.value ?? "").trim();
        if (!name) { nameEl?.focus(); return; }
        void addFeature(vid, name, coerceStatus(statusEl?.value));
        return;
      }
    }

    function onChange(e: Event): void {
      const t = e.target as HTMLElement | null;
      const sel = t?.closest<HTMLSelectElement>(".rm-status-select");
      if (!sel) return;
      const id = sel.dataset["feature"];
      if (id) void updateStatus(id, coerceStatus(sel.value));
    }

    container.addEventListener("click", onClick);
    container.addEventListener("change", onChange);

    void load();

    return () => {
      ac.abort();
      container.removeEventListener("click", onClick);
      container.removeEventListener("change", onChange);
    };
  },
};

registerPage(page);

export default page;
